import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import { SetProgress } from "@/components/organisms/SBCs/SetProgress";
import Card from "@/design-system/Card/Card";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import {
  getObjectivesSelector,
  isInObjectiveBundleSelector,
} from "@/redux/objectives/objectivesSlice";
import { tierSelector } from "@/redux/players/playersSlice";
import type { ObjectiveGroup } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import {
  formatTime,
  objectiveGroupPath,
  objectiveReward,
} from "@repo/shared/utils";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";

import { AddToBundleButton } from "./AddToBundleButton";

/** The SBC set card's layout: name, count and expiry, then the reward band. */
export const ObjectiveGroupCard = ({ group }: { group: ObjectiveGroup }) => {
  const { t } = useTranslation("objectives");
  const { liveEvents } = useSelector(getObjectivesSelector).groups;
  const { t: tSbcs } = useTranslation("sbcs");
  const reward = objectiveReward(group.rewards[0], t);
  const inBundle = useSelector(isInObjectiveBundleSelector(group.id));
  const tier = useSelector(
    tierSelector(
      reward?.metaRating?.metarating || 0,
      reward?.metaRating?.playerRoleId,
      true,
    ),
  );

  const repeatable =
    !!group.repeatabilityMode && group.repeatabilityMode !== "NON_REPEATABLE";
  const repeats = group.repeats ?? 0;
  const refreshInterval = group.refreshInterval ?? 0;
  const timeUntilRefresh =
    refreshInterval -
    ((Math.floor(Date.now() / 1000) - (group.startTime ?? 0)) %
      refreshInterval);
  // The SBC set card's timings line.
  const timings = [
    repeatable
      ? repeats > 0
        ? tSbcs("set.repeatableWithCount", { count: repeats })
        : tSbcs("set.repeatable")
      : tSbcs("set.nonRepeatable"),
    repeatable &&
      repeats > 0 &&
      refreshInterval > 0 &&
      tSbcs("setCard.refreshesIn", {
        time: formatTime(timeUntilRefresh * 1000),
      }),
    group.endTime
      ? t("card.expiresIn", {
          time: formatTime(group.endTime * 1000 - Date.now()),
        })
      : t("card.noExpiry"),
  ]
    .filter(Boolean)
    .join(" · ");
  const required = group.nObjectivesRequired ?? group.nObjectives;
  const meta = [
    group.nObjectivesRequired === undefined
      ? t("card.challengeCount", { count: group.nObjectives })
      : t("card.requiredCount", { count: group.nObjectives, required }),
    timings,
  ].join(" · ");

  return (
    <NavLink to={objectiveGroupPath(group)} className="h-full">
      <Card
        hoverable={false}
        className={cn(
          "relative flex h-full cursor-pointer flex-col overflow-hidden desktop:p-2",
          inBundle && "border-neutral-400",
        )}
      >
        <div className="flex flex-col gap-y-2 px-5 pb-4 pt-5">
          <h3 className="line-clamp-2 text-base font-semibold leading-tight text-foreground mobile:text-lg">
            {group.name}
          </h3>
          {group.completedCount === undefined && (
            <span className="truncate text-[11px] text-foreground-faint">
              {meta}
            </span>
          )}
        </div>

        {/* Signed in: the SBC set card's progress row, with the timings as its meta. */}
        {group.completedCount !== undefined && (
          <SetProgress
            className="px-5 pb-4"
            solvedCount={Math.min(group.completedCount, required)}
            sbcsCount={required}
            meta={
              <span className="truncate text-[11px] text-foreground-faint">
                {timings}
              </span>
            }
          />
        )}

        <div className="flex-1" />

        <RewardBand
          reward={reward}
          rewardUrl={extractRewardUrl(reward)}
          description={group.description}
          tier={tier}
        />

        {/* The whole foot of the card, like the SBC tile's Build Solutions. */}
        <div className="px-5 pb-5 pt-4">
          <AddToBundleButton
            groupId={group.id}
            matchModes={group.matchModes}
            liveEvents={liveEvents}
            stretch
            completed={group.completed}
            locked={group.locked}
          />
        </div>
      </Card>
    </NavLink>
  );
};
